import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { manageUrl, TYPE_SETTINGS } from '../cms/manageAreas';
import { useQuery } from '@apollo/client';
import { Search } from 'lucide-react';
import { useAccount } from '../../context/AccountContext';
import { useAuth } from '../../context/AuthContext';
import { useProduct, PRODUCT_HOME, PRODUCT_NAMES, ProductId } from '../../context/ProductContext';
import { useTheme } from '../../context/ThemeContext';
import { AgentStatus } from '../cms/AgentStatus';
import { FinishSignup } from '../cms/FinishSignup';
import { PausedAccess } from '../cms/PausedAccess';
import { InkToast } from '../cms/InkToast';
import { InkConfirm } from '../cms/InkConfirm';
import { AskAgentHost } from '../cms/AskYourAgent';
import { SiteSwitcher } from '../cms/SiteSwitcher';
import { ApprovalsTopButton } from '../approvals/ApprovalsReview';
import { useAddons } from '../cms/addons';
import { UsageTracking } from '../common/UsageTracking';
import { HelpFeedback } from '../common/HelpFeedback';
import { GET_TINYCMS_UNREAD_RESPONSES, GET_USER_PROFILE } from '../../graphql';
import { initialsFor } from '../../lib/initials';
import { AccountSwitcher } from './AccountSwitcher';
import { SwallowMark } from '../common/SwallowMark';
import { TinycmsWordmark } from '../common/TinycmsWordmark';
import { AvatarImage } from '../common/Avatar';
import { moreActive, SECTION_TITLES, WebsiteSection, websiteSection } from './planNav';

/**
 * Signed-in chrome from the Ink system (src/styles/ink.css). Four places in a
 * top bar; account lives behind the avatar. Product shells share the bar and
 * swap the places.
 */

interface Place {
  to: string;
  label: string;
  end?: boolean;
  active: (path: string, view?: string | null) => boolean;
}

/** Free, Personal: Today · Pages · … · Forms · Manage · More. Business adds Analytics. */
function flatPlaces(fullAnalytics: boolean): Place[] {
  const section = (id: WebsiteSection): Place => ({
    to: `/content?view=${id}`,
    label: SECTION_TITLES[id],
    active: (p, view) => websiteSection(p, view) === id,
  });
  return [
    { to: '/today', label: 'Today', end: true, active: (p) => p === '/today' },
    section('pages'),
    section('articles'),
    section('media'),
    section('authors'),
    section('forms'),
    ...(fullAnalytics ? [section('analytics')] : []),
    section('manage'),
    { to: '/more', label: 'More', active: (p, view) => moreActive(p, view, fullAnalytics) },
  ];
}

function isAmplifyPath(path: string) {
  return (
    path.startsWith('/reach/calendar') ||
    path.startsWith('/reach/campaigns') ||
    path.startsWith('/reach/automatic') ||
    isCampaignsPath(path)
  );
}

function isCampaignsPath(path: string) {
  return path === '/campaigns' || path.startsWith('/campaigns/');
}

function isPrismAiPath(path: string) {
  return path === '/prism-ai' || path.startsWith('/prism-ai/');
}

function isReachPath(path: string) {
  return path === '/reach' || path.startsWith('/reach/');
}

function isAddonPath(path: string) {
  return path.startsWith('/addons') || isAmplifyPath(path) || isPrismAiPath(path);
}

const TINYCMS_VIEWS: Place[] = [
  { to: '/today', label: 'Today', active: (p) => p === '/today' },
  {
    to: '/content',
    label: 'Website',
    active: (p) => p.startsWith('/content') || p.startsWith('/media'),
  },
  { to: '/site', label: 'Site', active: (p) => p === '/site' || p.startsWith('/site/') },
];

const PRISM_AI_VIEWS: Place[] = [
  {
    to: '/prism-ai/research',
    label: 'Research',
    active: (p) => p.startsWith('/prism-ai/research'),
  },
  {
    to: '/prism-ai/write',
    label: 'Write',
    active: (p) => p.startsWith('/prism-ai/write'),
  },
];

/**
 * Views of an add-on, shown under the host shell's menu. tinycms has none:
 * Prism AI and Reach are top-level there, with their own tabs.
 */
function addonViews(host: ProductId, path: string): { title: string; places: Place[] } | null {
  if (host === 'reach') {
    if (
      path.startsWith('/today') ||
      path.startsWith('/content') ||
      path === '/site' ||
      path.startsWith('/site/')
    ) {
      return { title: 'tinycms', places: TINYCMS_VIEWS };
    }
    if (isPrismAiPath(path)) return { title: 'Prism AI', places: PRISM_AI_VIEWS };
  }
  return null;
}

const PLACES: Record<ProductId, Place[]> = {
  tinycms: [
    { to: '/today', label: 'Today', end: true, active: (p) => p === '/today' },
    {
      to: '/content',
      label: 'Website',
      active: (p) => p.startsWith('/content') || p.startsWith('/media'),
    },
    { to: '/site', label: 'Site', active: (p) => p === '/site' || p.startsWith('/site/') },
    { to: '/addons', label: 'Add-ons', active: (p) => isAddonPath(p) },
  ],
  reach: [
    { to: '/', label: 'Today', end: true, active: (p) => p === '/' },
    { to: '/reach/calendar', label: 'Calendar', active: (p) => p.startsWith('/reach/calendar') },
    { to: '/reach/campaigns', label: 'Campaigns', active: (p) => p.startsWith('/reach/campaigns') },
    {
      to: '/addons',
      label: 'Add-ons',
      active: (p) =>
        p.startsWith('/addons') ||
        p.startsWith('/today') ||
        p.startsWith('/content') ||
        p === '/site' ||
        p.startsWith('/site/') ||
        isPrismAiPath(p),
    },
  ],
  // tinycms ("tinycms UX optimization review v6", Prototype v6): the site's
  // look and settings live under Content; Prism AI and Reach replace Add-ons.
  // Both come with Pro, and every site sees both: one without it gets the way
  // to Pro there (cms/Sections.tsx). Order (Prototype, backlog 6b9aeb20 /
  // bf819be4): Today · Campaigns · Reach · Content · Prism AI — Prism AI, the
  // tools that work across them, last. Campaigns only where Reach is (Pro):
  // AppShell leaves it out elsewhere, as an empty tab looks broken, and puts
  // Content next to Today there.
  atomizer: [
    { to: '/today', label: 'Today', end: true, active: (p) => p === '/today' },
    { to: '/campaigns', label: 'Campaigns', active: (p) => isCampaignsPath(p) },
    { to: '/reach/calendar', label: 'Social', active: (p) => isReachPath(p) },
    {
      to: '/content',
      label: 'Website',
      active: (p) =>
        p.startsWith('/content') ||
        p.startsWith('/media') ||
        p === '/site' ||
        p.startsWith('/site/'),
    },
    { to: '/prism-ai/research', label: 'Prism AI', active: (p) => isPrismAiPath(p) },
  ],
  collect: [
    { to: '/collection', label: 'Collection', active: (p) => p.startsWith('/collection') },
    { to: '/capture', label: 'Capture', active: (p) => p.startsWith('/capture') },
    { to: '/media', label: 'Media', active: (p) => p.startsWith('/media') },
  ],
};

// The avatar menu (UX optimization review, 2026-10-24): Profile settings and
// Account settings, then Sign out. Search (⌘K) still finds each Account section.
const MENU_LINKS: Array<{ label: string; sub?: string; to: string; hint?: string }> = [
  { label: 'Profile settings', sub: 'Name, sign-in, notifications, sign out', to: '/profile' },
  {
    label: 'Account settings',
    sub: 'AI agents, team, billing, API & sources',
    to: '/settings',
    hint: '⌘,',
  },
];
const ACCOUNT_LINKS: Array<{ label: string; to: string; siteOnly?: boolean; sub?: string }> = [
  { label: 'Profile settings', to: '/profile' },
  { label: 'General', to: '/settings/general' },
  { label: 'Voice & style', to: '/settings/voice', siteOnly: true },
  { label: 'AI Agents', to: '/settings/ai-agents', siteOnly: true },
  {
    label: 'Approvals',
    to: '/settings/approvals',
    siteOnly: true,
    sub: 'What goes live without a check in tinycms; require approval for everything.',
  },
  { label: 'Team', to: '/settings/team' },
  { label: 'Billing', to: '/settings/billing' },
  { label: 'API & sources', to: '/settings/connections' },
  { label: 'Activity Log', to: '/settings/activity' },
];

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { product, entitled, isPinned, setProduct } = useProduct();
  const { selectedAccount, accountId } = useAccount();
  // The Campaign role (2026-10-23): only the campaigns they're on — no other
  // tab, no search, no settings beyond their own profile.
  const campaignRole = Boolean(selectedAccount?.campaignRole);
  const { logout } = useAuth();
  const { data: meData } = useQuery<{
    me?: { id?: string; email?: string; name?: string | null; photoUrl?: string | null };
  }>(GET_USER_PROFILE);
  const userEmail = meData?.me?.email || '';
  // The name the app uses for a person: the one on their profile, else the
  // email's local part. Both and the photo come from the users row, so a
  // change on Profile settings shows here at once (same query, same cache).
  const userName = meData?.me?.name || userEmail.split('@')[0];
  const userInitials = initialsFor(meData?.me?.name, userEmail);
  const photoUrl = meData?.me?.photoUrl || '';
  const { theme, toggle } = useTheme();
  const [menu, setMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const [query, setQuery] = useState('');
  const addon = addonViews(product, location.pathname);
  const { shellRef, headerRef, addonRef, mainRef, markRef, scrolled } = useScrolledUnder(
    Boolean(addon)
  );
  // Screens scroll with the page now, so a new screen (or Content view) starts
  // at the top rather than where the last one was left.
  const view = new URLSearchParams(location.search).get('view');
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname, view, mainRef]);

  // New form responses: a count on Content, checked every minute (nothing is emailed).
  const { data: unreadData } = useQuery(GET_TINYCMS_UNREAD_RESPONSES, {
    variables: { accountId },
    skip: product !== 'atomizer' || !accountId || campaignRole,
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const unreadResponses: number = unreadData?.tinycmsUnreadResponses || 0;
  // The Approvals review: everything waiting on the person, with one count.
  const approvalsOn = product === 'atomizer' && !campaignRole;

  const { flatNav, fullAnalytics } = useAddons();
  const flat = product === 'atomizer' && flatNav;
  const places = campaignRole
    ? PLACES.atomizer.filter((p) => p.to === '/campaigns')
    : flat
      ? flatPlaces(fullAnalytics)
      : PLACES[product];
  const { navRef, navFade, checkNav } = useNavFade(
    places.map((p) => p.to).join(' '),
    `${location.pathname} ${view} ${accountId}`
  );

  // Anywhere else (an old link, the start page) goes to Campaigns.
  useEffect(() => {
    if (!campaignRole) return;
    const p = location.pathname;
    if (isCampaignsPath(p) || p === '/profile') return;
    navigate('/campaigns', { replace: true });
  }, [campaignRole, location.pathname, navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // No search or settings shortcuts for the Campaign role.
      if (campaignRole && (e.metaKey || e.ctrlKey) && ['k', ','].includes(e.key.toLowerCase())) {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
        setQuery('');
      } else if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        navigate('/settings');
      } else if (e.key === 'Escape') {
        setPalette(false);
        setMenu(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, campaignRole]);

  const commands = useMemo(() => {
    const rows: Array<{
      label: string;
      to: string;
      group: string;
      product?: ProductId;
      sub?: string;
    }> = [
      // The flat nav's Website sections are already listed under Website below.
      ...places
        .filter((p) => !p.to.startsWith('/content?'))
        .map((p) => ({ label: p.label, to: p.to, group: PRODUCT_NAMES[product] })),
      ...(product === 'atomizer' || product === 'tinycms'
        ? [
            { label: 'Pages', to: '/content?view=pages', group: 'Website' },
            { label: 'Articles', to: '/content?view=articles', group: 'Website' },
            { label: 'Media', to: '/content?view=media', group: 'Website' },
            { label: 'Authors', to: '/content?view=authors', group: 'Website' },
            { label: 'Forms', to: '/content?view=forms', group: 'Website' },
            { label: 'Analytics', to: '/content?view=analytics', group: 'Website' },
            { label: 'Manage', to: '/content?view=manage', group: 'Website' },
            { label: 'Manage › Site', to: manageUrl('site'), group: 'Website' },
            ...Object.values(TYPE_SETTINGS).map(({ label, to }) => ({
              label,
              to,
              group: 'Website',
            })),
            { label: 'Design', to: '/content?view=manage&area=design', group: 'Website' },
            { label: 'All sites', to: '/sites', group: 'Sites' },
            { label: 'New site', to: '/sites?new=1', group: 'Sites' },
          ]
        : []),
      ...ACCOUNT_LINKS.filter(
        (l) => !l.siteOnly || product === 'tinycms' || product === 'atomizer'
      ).map((l) => ({ label: l.label, to: l.to, group: 'Account', sub: l.sub })),
      ...entitled
        .filter((id) => id !== product)
        .map((id) => ({
          label: PRODUCT_NAMES[id],
          to: PRODUCT_HOME[id],
          group: 'Switch product',
          product: id,
        })),
    ];
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.label} ${r.sub || ''}`.toLowerCase().includes(q)) : rows;
  }, [places, product, entitled, query]);

  const go = (to: string, nextProduct?: ProductId) => {
    if (nextProduct && !isPinned) setProduct(nextProduct);
    setPalette(false);
    setMenu(false);
    navigate(to);
  };

  return (
    <div className="ink-shell" ref={shellRef}>
      <header className="ink-header" ref={headerRef} data-scrolled={scrolled ? 'true' : undefined}>
        <button className="ink-brand" onClick={() => navigate(PRODUCT_HOME[product])}>
          {product === 'tinycms' || product === 'atomizer' ? (
            <SwallowMark className="ink-mark-swallow" />
          ) : (
            <span className="ink-mark" />
          )}
          <span className="ink-brand-name">
            {product === 'tinycms' || product === 'atomizer' ? (
              <TinycmsWordmark />
            ) : (
              PRODUCT_NAMES[product]
            )}
          </span>
        </button>
        <span className="ink-head-sep">/</span>
        <span className="ink-account-name">
          {campaignRole ? (
            <span className="st-btn-name" style={{ padding: '5px 8px' }}>
              {selectedAccount?.name}
            </span>
          ) : product === 'atomizer' || product === 'tinycms' ? (
            <SiteSwitcher />
          ) : (
            <AccountSwitcher variant="inline" showWhenSingle />
          )}
        </span>
        <nav
          className="ink-nav"
          ref={navRef}
          onScroll={checkNav}
          data-fade={navFade ? 'true' : undefined}
        >
          {places.map((place) => (
            <button
              key={place.to}
              data-active={place.active(location.pathname, view) ? 'true' : 'false'}
              onClick={() => navigate(place.to)}
            >
              {place.label}
              {place.label === (flat ? 'Forms' : 'Website') &&
              product === 'atomizer' &&
              unreadResponses > 0 ? (
                <span className="ink-badge" title={`${unreadResponses} new form responses`}>
                  {unreadResponses}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="ink-header-tools">
          {approvalsOn ? <ApprovalsTopButton /> : null}
          {campaignRole ? null : (
            <button
              className="ink-search-btn"
              onClick={() => setPalette(true)}
              aria-label="Search (⌘K)"
              title="Search  ⌘K"
            >
              {/* The glass alone, a 30px square; ⌘K still opens it and shows on
                  hover (Prototype 2026-10-25). */}
              <Search size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
          {(product === 'atomizer' || product === 'tinycms') && !campaignRole ? (
            <AgentStatus />
          ) : null}
          {/* Help & feedback: a person on the tinycms team, never an AI. */}
          <HelpFeedback email={userEmail} />
          <button
            className="ink-icon-btn"
            title={theme === 'dark' ? 'Light' : 'Dark'}
            aria-label="Toggle appearance"
            onClick={toggle}
          >
            <span
              style={{
                width: 13,
                height: 13,
                borderRadius: '50%',
                border: '1.5px solid var(--ink-ink)',
                background: 'linear-gradient(90deg, var(--ink-ink) 50%, transparent 50%)',
              }}
            />
          </button>
          <button
            className="ink-avatar"
            data-here={location.pathname.startsWith('/settings') ? 'true' : 'false'}
            onClick={() => setMenu((v) => !v)}
            aria-label="Account"
          >
            <AvatarImage url={photoUrl} initials={userInitials} />
          </button>
          {menu ? (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
                onClick={() => setMenu(false)}
              />
              <div className="ink-menu">
                {/* Who is signed in, and on which site (Prototype v13). */}
                <div className="ink-menu-label">
                  <div style={{ fontWeight: 600 }}>{userName || 'Signed in'}</div>
                  <div className="ink-muted" style={{ marginTop: 2 }}>
                    {[userEmail, selectedAccount?.name || PRODUCT_NAMES[product]]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                {MENU_LINKS.filter((link) => !campaignRole || link.to === '/profile').map(
                  (link) => (
                    <button key={link.to} onClick={() => go(link.to)}>
                      <span className="ink-menu-item-text">
                        {link.label}
                        {link.sub ? <span className="ink-menu-item-sub">{link.sub}</span> : null}
                      </span>
                      {link.hint ? <span className="ink-kbd ink-muted">{link.hint}</span> : null}
                    </button>
                  )
                )}
                {!isPinned &&
                !campaignRole &&
                entitled.filter((id) => id !== product).length > 0 ? (
                  <>
                    <div className="ink-menu-rule" />
                    {entitled
                      .filter((id) => id !== product)
                      .map((id) => (
                        <button key={id} onClick={() => go(PRODUCT_HOME[id], id)}>
                          {PRODUCT_NAMES[id]}
                        </button>
                      ))}
                  </>
                ) : null}
                <div className="ink-menu-rule" />
                <button onClick={() => logout('You have been successfully logged out.')}>
                  Sign out
                </button>
              </div>
            </>
          ) : null}
        </div>
      </header>
      {(() => {
        if (!addon) return null;
        return (
          <div className="ink-addon-bar" ref={addonRef}>
            <span className="ink-addon-name">{addon.title}</span>
            <nav>
              {addon.places.map((place) => (
                <button
                  key={place.to}
                  type="button"
                  data-active={place.active(location.pathname) ? 'true' : 'false'}
                  onClick={() => navigate(place.to)}
                >
                  {place.label}
                </button>
              ))}
            </nav>
          </div>
        );
      })()}
      <div className="ink-main" ref={mainRef}>
        <span className="ink-scroll-mark" ref={markRef} aria-hidden="true" />
        <PausedAccess>
          {/* A site switch remounts the screen: a page, form, post or campaign
              picked on one site never stays picked on the other. */}
          <React.Fragment key={accountId || 'none'}>
            <Outlet />
          </React.Fragment>
          {product === 'atomizer' && !campaignRole ? <FinishSignup /> : null}
        </PausedAccess>
      </div>
      <InkToast />
      <InkConfirm />
      <AskAgentHost />
      {/* App usage, with consent; the one-time ask (backlog 1d013d63). */}
      <UsageTracking />
      {palette ? (
        <div className="ink-palette" onClick={() => setPalette(false)}>
          <div className="ink-palette-card" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              placeholder="Go to a screen"
              aria-label="Go to a screen"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div style={{ maxHeight: 320, overflow: 'auto', padding: '6px 0' }}>
              {commands.length === 0 ? (
                <div className="ink-muted" style={{ padding: '12px 16px' }}>
                  Nothing matches.
                </div>
              ) : (
                commands.map((cmd) => (
                  <button key={cmd.group + cmd.label} onClick={() => go(cmd.to, cmd.product)}>
                    <span className="ink-muted" style={{ marginRight: 10, fontWeight: 400 }}>
                      {cmd.group}
                    </span>
                    {cmd.label}
                    {cmd.sub ? <span className="ink-palette-sub">{cmd.sub}</span> : null}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The page scrolls under the top bar, which frosts once it has (Motion and
 * Brand spec §1.2). The bar's height (and the add-on bar's) goes on the shell
 * as --ink-top, so .ink-main starts under it; a 1px mark at the top of
 * .ink-main says when anything has scrolled up behind it. No scroll handler.
 */
function useScrolledUnder(hasAddonBar: boolean) {
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const addonRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const [top, setTop] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => {
      const h = header.offsetHeight + (addonRef.current?.offsetHeight || 0);
      shellRef.current?.style.setProperty('--ink-top', `${h}px`);
      setTop(h);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(header);
    if (addonRef.current) ro?.observe(addonRef.current);
    return () => ro?.disconnect();
  }, [hasAddonBar]);

  useEffect(() => {
    const main = mainRef.current;
    const mark = markRef.current;
    if (!main || !mark || typeof IntersectionObserver === 'undefined') return;
    // The mark sits just under the bar; it leaves the area below the bar as
    // soon as the page moves.
    const io = new IntersectionObserver(([e]) => setScrolled(!e.isIntersecting), {
      root: main,
      rootMargin: `-${top}px 0px 0px 0px`,
    });
    io.observe(mark);
    return () => io.disconnect();
  }, [top]);

  return { shellRef, headerRef, addonRef, mainRef, markRef, scrolled };
}

/**
 * When the places don't fit they slide sideways; while there's more to the
 * right, the row fades out at its edge (Motion and Brand spec §1.3). Any
 * width, not only below 900px: at ~920px "Prism AI" was cut off with no cue
 * (739d1b83). A new screen, view or site brings its pill into sight, 48px clear
 * of the fade (setting scrollLeft: scrollIntoView could scroll the page too).
 */
function useNavFade(placesKey: string, screenKey: string) {
  const navRef = useRef<HTMLElement>(null);
  const [navFade, setNavFade] = useState(false);
  const checkNav = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    setNavFade(
      nav.scrollWidth > nav.clientWidth + 2 &&
        nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2
    );
  }, []);
  const showActive = useCallback(() => {
    const nav = navRef.current;
    const pill = nav?.querySelector<HTMLElement>('[data-active="true"]');
    if (nav && pill) {
      const box = nav.getBoundingClientRect();
      const rect = pill.getBoundingClientRect();
      const left = rect.left - box.left + nav.scrollLeft;
      const right = left + rect.width;
      if (left < nav.scrollLeft) nav.scrollLeft = left - 8;
      else if (right + 48 > nav.scrollLeft + nav.clientWidth) {
        nav.scrollLeft = right + 48 - nav.clientWidth;
      }
    }
    checkNav();
  }, [checkNav]);
  useEffect(() => {
    showActive();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(showActive) : null;
    if (navRef.current) ro?.observe(navRef.current);
    window.addEventListener('resize', showActive, { passive: true });
    // The web font changes the pills' widths without resizing the row.
    void document.fonts?.ready.then(showActive);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', showActive);
    };
  }, [showActive, placesKey, screenKey]);
  return { navRef, navFade, checkNav };
}
